import React, { useEffect, type ComponentType, type ReactNode, type RefObject } from 'react';
import DsButton from '../../DsButton';
import RawDsModal from '../../DsModal';
import useIsMobileLayout from '../../../../hooks/useIsMobileLayout';
import { useChatTopicsContext } from './ChatTopicsContext';
import '../../../../css/common/chat/ChatMessageActionMenu.css';

const DsModal = RawDsModal as ComponentType<any>;

// Callers supply their existing permission-filtered actions. The same panel
// adds batch actions without changing the message those callbacks act on.
function messageActions(children: ReactNode): ReactNode {
  return React.Children.map(children, (child) => {
    if (!React.isValidElement<any>(child)) return child;
    if (child.type === React.Fragment) return <>{messageActions(child.props.children)}</>;
    if (child.type !== 'button') return child;
    const destructive = child.props.className?.includes('--danger');
    return <DsButton {...child.props} variant="ghost" className={`chat-message-action${destructive ? ' chat-message-action--danger' : ''}`} />;
  });
}

export default function ChatMessageActionMenu({ menu, menuRef, onClose, reactions, children }: {
  menu: any; menuRef?: RefObject<HTMLDivElement>; onClose: () => void; reactions?: ReactNode; children: ReactNode;
}) {
  const topics = useChatTopicsContext();
  const selection = topics?.selection;
  const selecting = Boolean(selection?.active);
  const compact = useIsMobileLayout();
  const open = selecting ? Boolean(selection.menu) : Boolean(menu);
  const single = selecting && selection.rows.length === 1 && String(menu?.message?.id) === String(selection.rows[0].id);
  const close = () => { selection?.closeMenu(); onClose(); };
  useEffect(() => {
    if (selecting && !selection.menu && menu) onClose();
  }, [selecting, selection?.menu, menu, onClose]);
  useEffect(() => {
    if (!open || selecting || compact) return;
    const keyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); onClose(); }
    };
    window.addEventListener('keydown', keyDown);
    return () => window.removeEventListener('keydown', keyDown);
  }, [open, selecting, compact, onClose]);
  if (!open) return null;
  const finishSingleAction = (event: React.MouseEvent) => {
    if (selecting && (event.target as Element).closest('button:not(:disabled)')) selection.finish();
  };
  const content = <div className="chat-message-actions" onClick={(event) => event.stopPropagation()}>
    {(!selecting || single) && reactions && <div onClickCapture={finishSingleAction}>{reactions}</div>}
    {selecting && <div className="chat-message-actions__batch">
      <DsButton variant="ghost" className="chat-message-action" disabled={selection.busy || !selection.rows.length} onClick={() => { close(); void selection.copy(); }}>
        <i className="fa-regular fa-copy" aria-hidden="true" /><span>まとめてコピー</span>
      </DsButton>
      <DsButton variant="ghost" className="chat-message-action" disabled={selection.busy || !selection.rows.length} onClick={() => {
        close();
        if (topics.topicId) void selection.execute().catch(() => {}); else selection.openPicker();
      }}><i className={`fa-solid ${topics.topicId ? 'fa-link-slash' : 'fa-list-check'}`} aria-hidden="true" /><span>{topics.topicId ? 'スレッドから外す' : 'スレッドに追加'}</span></DsButton>
    </div>}
    {(!selecting || single) && <div className={`chat-message-actions__single${selecting ? ' chat-message-actions__single--separated' : ''}`}
      onClickCapture={finishSingleAction}>
      {messageActions(children)}
      {!selecting && selection?.eligible(menu?.message) && <DsButton variant="ghost" className="chat-message-action"
        disabled={selection.busy} onClick={() => { close(); selection.start(menu.message); }}>
        <i className="fa-regular fa-square-check" aria-hidden="true" /><span>メッセージを選択</span>
      </DsButton>}
    </div>}
  </div>;
  // Touch layouts and selection share the accessible modal. Desktop ordinary
  // right-click keeps the viewport-bounded menu beside the clicked message.
  if (selecting || compact) return <DsModal open title={selecting ? `${selection.rows.length}件のメッセージ` : 'メッセージ'}
    size="sm" dismissible={!selection?.busy} onClose={close} bodyClassName="chat-message-action-body">{content}</DsModal>;
  return <div className="chat-message-action-overlay" onClick={close}>
    <div ref={menuRef} className="chat-message-action-popover ds-compact-action-menu" role="menu" aria-label="メッセージの操作"
      style={{ top: menu.y, left: menu.x }}>{content}</div>
  </div>;
}
